
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';
/**
 * Helper function to process timestamps from backend
 * @param {Object} data - Data object from backend
 * @returns {Object} Data with timestamps in expected format
 */
const processTimestamps = (data) => {
  return {
    ...data,
    // Backend returns ISO strings, no conversion needed
  };
};

/**
 * Secure Inventory Service
 * Uses backend API endpoints instead of direct Firestore access
 */

// ===== SUPPLIERS =====

/**
 * Get all suppliers for a facility
 * @param {string} facilityId - Facility ID (optional for admins)
 * @param {boolean} activeOnly - Whether to get only active suppliers
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Suppliers with pagination
 */
export const getSuppliers = async (facilityId = null, activeOnly = false, page = 1, limit = 50) => {
  try {
    console.log('Fetching suppliers for facility:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    if (facilityId) params.append('facilityId', facilityId);
    if (activeOnly) params.append('active', 'true');
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await axios.get(
      `${API_BASE}/admin/inventory/suppliers?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      suppliers: response.data.suppliers.map(processTimestamps),
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

/**
 * Create a new supplier
 * @param {Object} supplierData - Supplier data
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Created supplier
 */
export const createSupplier = async (supplierData, facilityId) => {
  try {
    console.log('Creating new supplier');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/admin/inventory/suppliers`,
      {
        ...supplierData,
        facilityId,
        active: true
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return processTimestamps(response.data);
  } catch (error) {
    console.error('Error creating supplier:', error);
    throw error;
  }
};

/**
 * Update an existing supplier
 * @param {string} supplierId - Supplier ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export const updateSupplier = async (supplierId, updateData) => {
  try {
    console.log('Updating supplier:', supplierId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/admin/inventory/suppliers/${supplierId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating supplier:', error);
    throw error;
  }
};

// ===== INVENTORY ITEMS =====

/**
 * Get all inventory items for a facility
 * @param {string} facilityId - Facility ID
 * @param {Object} filters - Filters (category, status, lowStock)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Items with pagination
 */
export const getInventoryItems = async (facilityId, filters = {}, page = 1, limit = 50) => {
  try {
    console.log('Fetching inventory items for facility:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/admin/inventory/items?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      items: response.data.items.map(processTimestamps),
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching inventory items:', error);
    throw error;
  }
};

/**
 * Get a specific inventory item by ID
 * @param {string} itemId - Item ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Inventory item
 */
export const getInventoryItemById = async (itemId, facilityId) => {
  try {
    console.log('Fetching inventory item:', itemId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/admin/inventory/items/${itemId}?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return processTimestamps(response.data.item);
  } catch (error) {
    console.error('Error fetching inventory item by ID:', error);
    throw error;
  }
};

/**
 * Create a new inventory item
 * @param {Object} itemData - Item data
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Created item
 */
export const createInventoryItem = async (itemData, facilityId) => {
  try {
    console.log('Creating new inventory item');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/admin/inventory/items`,
      {
        ...itemData,
        facilityId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return processTimestamps(response.data);
  } catch (error) {
    console.error('Error creating inventory item:', error);
    throw error;
  }
};

/**
 * Update an existing inventory item
 * @param {string} itemId - Item ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export const updateInventoryItem = async (itemId, updateData) => {
  try {
    console.log('Updating inventory item:', itemId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/admin/inventory/items/${itemId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating inventory item:', error);
    throw error;
  }
};

/**
 * Delete an inventory item
 * @param {string} itemId - Item ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteInventoryItem = async (itemId, facilityId) => {
  try {
    console.log('Deleting inventory item:', itemId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/admin/inventory/items/${itemId}?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting inventory item:', error);
    throw error;
  }
};

// ===== INVENTORY BATCHES =====

/**
 * Get all batches for a facility or specific item
 * @param {string} facilityId - Facility ID
 * @param {Object} filters - Filters (itemId, status, expiringSoon)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Batches with pagination
 */
export const getInventoryBatches = async (facilityId, filters = {}, page = 1, limit = 50) => {
  try {
    console.log('Fetching inventory batches for facility:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/admin/inventory/batches?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      batches: response.data.batches.map(processTimestamps),
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching inventory batches:', error);
    throw error;
  }
};

/**
 * Create a new inventory batch
 * @param {Object} batchData - Batch data
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Created batch
 */
export const createInventoryBatch = async (batchData, facilityId) => {
  try {
    console.log('Creating new inventory batch');
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/admin/inventory/batches`,
      {
        ...batchData,
        facilityId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return processTimestamps(response.data);
  } catch (error) {
    console.error('Error creating inventory batch:', error);
    throw error;
  }
};

/**
 * Update inventory stock for an item
 * @param {string} itemId - Item ID
 * @param {number} quantity - Quantity change (positive for increase, negative for decrease)
 * @param {string} reason - Reason for stock change
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Update result
 */
export const updateInventoryStock = async (itemId, quantity, reason, facilityId) => {
  try {
    console.log('Updating inventory stock for item:', itemId, 'quantity:', quantity);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/admin/inventory/items/${itemId}/stock-adjustment`,
      {
        quantity,
        reason,
        facilityId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating inventory stock:', error);
    throw error;
  }
};

/**
 * Get low stock items for a facility
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Array>} Items with low stock
 */
export const getLowStockItems = async (facilityId) => {
  try {
    console.log('Fetching low stock items for facility:', facilityId);
    const result = await getInventoryItems(facilityId, { lowStock: true });
    return result.items;
  } catch (error) {
    console.error('Error fetching low stock items:', error);
    throw error;
  }
};

/**
 * Get expiring batches for a facility
 * @param {string} facilityId - Facility ID
 * @param {number} daysAhead - Number of days ahead to check for expiration
 * @returns {Promise<Array>} Expiring batches
 */
export const getExpiringBatches = async (facilityId, daysAhead = 30) => {
  try {
    console.log('Fetching expiring batches for facility:', facilityId);
    const result = await getInventoryBatches(facilityId, { expiringSoon: true });
    return result.batches;
  } catch (error) {
    console.error('Error fetching expiring batches:', error);
    throw error;
  }
};

/**
 * Generate inventory report for a facility
 * @param {string} facilityId - Facility ID
 * @param {Object} reportOptions - Report options (dateRange, categories, etc.)
 * @returns {Promise<Object>} Inventory report data
 */
export const generateInventoryReport = async (facilityId, reportOptions = {}) => {
  try {
    console.log('Generating inventory report for facility:', facilityId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/admin/inventory/reports`,
      {
        facilityId,
        ...reportOptions
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.report;
  } catch (error) {
    console.error('Error generating inventory report:', error);
    throw error;
  }
};

/**
 * Search inventory items by name or code
 * @param {string} facilityId - Facility ID
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Matching inventory items
 */
export const searchInventoryItems = async (facilityId, searchTerm) => {
  try {
    console.log('Searching inventory items for term:', searchTerm);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('search', searchTerm);
    
    const response = await axios.get(
      `${API_BASE}/admin/inventory/items/search?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.items.map(processTimestamps);
  } catch (error) {
    console.error('Error searching inventory items:', error);
    throw error;
  }
};

/**
 * inventoryService object - for components that import the service as an object
 */
export const inventoryService = {
  getSuppliers,
  createSupplier,
  updateSupplier,
  getInventoryItems,
  getInventoryItemById,
  createInventoryItem,
  updateInventoryItem,
  deleteInventoryItem,
  getInventoryBatches,
  createInventoryBatch,
  updateInventoryStock,
  getLowStockItems,
  getExpiringBatches,
  generateInventoryReport,
  searchInventoryItems
};